import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { Flame, CheckCircle2, Calendar, Dumbbell, ArrowRight, Loader2, Gift } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1534258936925-c58bed479fcb?auto=format&fit=crop&w=1600&q=80";

export default function ChallengePage() {
    const [info, setInfo] = useState(null);
    const [params] = useSearchParams();
    const [form, setForm] = useState({ name: "", email: "", password: "", goal: "", experience: "Beginner", promo_code: params.get("promo") || "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null);
    const { refresh } = useAuth();
    const nav = useNavigate();

    useEffect(() => { api.get("/challenge/info").then(({ data }) => setInfo(data)).catch(() => {}); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const { data } = await api.post("/challenge/enroll", form);
            setToken(data.token);
            await refresh();
            // Best-effort promo redemption if provided
            if (form.promo_code?.trim()) {
                try { await api.post("/promo/redeem", { code: form.promo_code.trim() }); } catch (_e) {}
            }
            setDone(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex items-center justify-center p-6" data-testid="challenge-success">
                <div className="max-w-xl w-full text-center">
                    <CheckCircle2 size={48} className="text-[var(--gold-bright)] mx-auto mb-6" />
                    <p className="label-tiny mb-3">/ You're in</p>
                    <h1 className="font-serif text-5xl md:text-6xl mb-4 leading-[1.05]">
                        Welcome to the <span className="gold-gradient-text">Challenge</span>.
                    </h1>
                    <p className="text-base text-[var(--text-muted)] max-w-md mx-auto mb-8">
                        {done.total_workouts} workouts scheduled starting <span className="text-[var(--gold)] font-mono">{new Date(done.first_workout_date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>.
                        Your athlete portal is ready.
                    </p>
                    <button onClick={() => nav("/client")} className="btn-primary inline-flex items-center gap-2" data-testid="challenge-go-portal">
                        Enter my portal <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="challenge-page">
            <header className="border-b border-[var(--border)]">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/"><Logo size="sm" /></Link>
                    <Link to="/login" className="btn-ghost text-xs">Sign in</Link>
                </div>
            </header>

            <section className="relative overflow-hidden border-b border-[var(--border)]">
                <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a0908] via-[#0a0908]/90 to-transparent" />
                <div className="relative max-w-6xl mx-auto px-6 py-16 lg:py-24">
                    <div className="inline-flex items-center gap-2 border border-[var(--gold)] bg-[var(--surface)]/60 px-3 py-1.5 mb-6">
                        <Flame size={12} className="text-[var(--gold-bright)]" />
                        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--gold-bright)]">Free · Limited spots</span>
                    </div>
                    <h1 className="font-serif text-5xl md:text-7xl mb-6 leading-[0.95] max-w-3xl">
                        2 weeks. 6 workouts. <span className="gold-gradient-text italic">Zero excuses.</span>
                    </h1>
                    <p className="text-base md:text-lg text-[var(--text-muted)] max-w-2xl leading-relaxed mb-8">
                        The <span className="text-[var(--text)]">2-Week Ignition Challenge</span> — bodyweight only, three sessions a week, every Monday, Wednesday and Friday. Built by King Seidorb to take you from &quot;I&apos;ll start Monday&quot; to <span className="text-[var(--gold)]">&quot;look at me now.&quot;</span>
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <div className="border border-[var(--border-bright)] bg-[var(--surface)]/60 px-4 py-3">
                            <p className="label-tiny mb-1">Schedule</p>
                            <p className="font-display text-lg">Mon · Wed · Fri</p>
                        </div>
                        <div className="border border-[var(--border-bright)] bg-[var(--surface)]/60 px-4 py-3">
                            <p className="label-tiny mb-1">Equipment</p>
                            <p className="font-display text-lg">Bodyweight only</p>
                        </div>
                        <div className="border border-[var(--border-bright)] bg-[var(--surface)]/60 px-4 py-3">
                            <p className="label-tiny mb-1">Duration</p>
                            <p className="font-display text-lg">{info?.total_workouts || 6} workouts · {info?.weeks || 2} weeks</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                    <p className="label-tiny mb-3">/ What you get</p>
                    <h2 className="font-serif text-4xl mb-6">Everything you need. Nothing you don't.</h2>
                    <ul className="space-y-4 text-sm">
                        {[
                            ["Structured 2-week bodyweight program", "Progressive overload — Foundation → Peak in 6 sessions"],
                            ["Your own athlete portal", "Check off every workout, track your streak, log your weight weekly"],
                            ["Built-in interval timer", "Tabata, HIIT 30/30, EMOM, and Strength presets — no side apps"],
                            ["Progress photos + weight chart", "Day-1 and Day-14 side-by-side — receipts for your transformation"],
                            ["Direct messaging with King", "Stuck on form? Ask. You'll get a reply."],
                        ].map(([title, sub], i) => (
                            <li key={i} className="flex gap-4">
                                <div className="flex items-center justify-center w-8 h-8 border border-[var(--gold-deep)] text-[var(--gold-bright)] shrink-0 font-mono text-xs">
                                    0{i + 1}
                                </div>
                                <div>
                                    <p className="font-medium mb-1">{title}</p>
                                    <p className="text-[var(--text-muted)] text-xs">{sub}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Enrollment form */}
                <div className="border border-[var(--gold)] bg-[var(--surface)] p-6 lg:p-8 relative overflow-hidden" data-testid="challenge-form-card">
                    <div className="absolute inset-0 bg-stripes opacity-10 pointer-events-none" />
                    <div className="relative">
                        <p className="label-tiny mb-2 text-[var(--gold-bright)]">/ Claim your spot</p>
                        <h3 className="font-serif text-3xl mb-6">Enroll — free</h3>
                        <form onSubmit={submit} className="space-y-4" data-testid="challenge-form">
                            <div>
                                <label className="label-tiny block mb-2">Your name</label>
                                <input required className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="What should I call you?" data-testid="challenge-name" />
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Email</label>
                                <input required type="email" className="input-dark" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="challenge-email" />
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Create a password</label>
                                <input required type="password" minLength={8} className="input-dark" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" data-testid="challenge-password" />
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Your main goal</label>
                                <select className="input-dark" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="challenge-goal">
                                    <option value="">Pick one…</option>
                                    <option>Fat loss</option>
                                    <option>Get stronger</option>
                                    <option>Build the habit</option>
                                    <option>Kickstart fitness after a break</option>
                                    <option>General health</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Training experience</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {["Beginner", "Intermediate", "Advanced"].map((x) => (
                                        <button key={x} type="button" onClick={() => setForm({ ...form, experience: x })}
                                            className={`py-2 text-xs font-mono tracking-[0.2em] uppercase border transition-colors ${form.experience === x ? "bg-[var(--gold)] text-[#0a0908] border-[var(--gold)]" : "border-[var(--border-bright)] hover:border-[var(--gold-deep)]"}`}
                                            data-testid={`challenge-exp-${x.toLowerCase()}`}>
                                            {x}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="label-tiny block mb-2 flex items-center gap-2">
                                    <Gift size={11} className="text-[var(--gold-bright)]" />
                                    Promo code <span className="text-[var(--text-faint)] normal-case tracking-normal">(optional)</span>
                                </label>
                                <input className="input-dark font-mono uppercase tracking-[0.2em]" value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })} placeholder="NDEFREE" data-testid="challenge-promo" />
                            </div>
                            {err && <div className="text-sm text-[var(--danger)]" data-testid="challenge-error">{err}</div>}
                            <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="challenge-submit">
                                {busy ? <><Loader2 size={14} className="animate-spin" /> Enrolling…</> : <><Dumbbell size={14} /> Start my 2 weeks</>}
                            </button>
                            <p className="text-[10px] text-center text-[var(--text-faint)] font-mono tracking-[0.2em] uppercase">Free forever · no card required</p>
                        </form>
                    </div>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 py-16 border-t border-[var(--border)]">
                <p className="label-tiny mb-3">/ Schedule</p>
                <h2 className="font-serif text-4xl mb-8">What the 14 days look like.</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)]">
                    {["Foundation", "PEAK"].map((label, i) => (
                        <div key={i} className={`bg-[var(--surface)] p-5 ${i === 1 ? "border-t-2 border-[var(--gold)]" : ""}`}>
                            <p className="label-tiny mb-1">Week {i + 1}</p>
                            <p className="font-serif text-2xl mb-3">{label}</p>
                            <div className="space-y-1 text-xs text-[var(--text-muted)] font-mono">
                                <div className="flex items-center gap-2"><Calendar size={10} className="text-[var(--gold)]" /> Mon</div>
                                <div className="flex items-center gap-2"><Calendar size={10} className="text-[var(--gold)]" /> Wed</div>
                                <div className="flex items-center gap-2"><Calendar size={10} className="text-[var(--gold)]" /> Fri</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
